import { devLog, devWarn, devError } from '../utils/devLogger';

// 네이버 SDK 초기화 및 유틸리티 함수
declare global {
  interface Window {
    naver: any;
    naverLogin: any;
  }
}

// 네이버 SDK 초기화
export const initNaverSDK = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    if (!window.naver) {
      devWarn('네이버 SDK가 아직 로드되지 않았습니다.');
      return false;
    }

    const clientId = process.env.REACT_APP_NAVER_CLIENT_ID;
    const callbackUrl = process.env.REACT_APP_NAVER_CALLBACK_URL || window.location.origin + '/login';
    
    if (!clientId) {
      devError('REACT_APP_NAVER_CLIENT_ID가 설정되지 않았습니다.');
      return false;
    }

    // 네이버 로그인 초기화
    const naverLogin = new window.naver.LoginWithNaverId({
      clientId: clientId,
      callbackUrl: callbackUrl,
      isPopup: false, // 리다이렉트 방식으로 변경
      callbackHandle: true
    });
    
    naverLogin.init();
    window.naverLogin = naverLogin;
    
    devLog('네이버 SDK 초기화 완료');
    return true;
  } catch (error) {
    devError('네이버 SDK 초기화 실패:', error);
    return false;
  }
};

// 네이버 로그인 상태 확인 및 사용자 정보 반환
export const getNaverUserInfo = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    try {
      if (!window.naverLogin) {
        reject(new Error('네이버 SDK가 로드되지 않았습니다.'));
        return;
      }

      window.naverLogin.getLoginStatus((status: boolean) => {
        devLog('네이버 로그인 상태 확인:', status);
        
        if (status) {
          try {
            const user = window.naverLogin.user;
            const accessToken = window.naverLogin.accessToken?.accessToken;
            
            devLog('네이버 사용자 정보:', user);
            devLog('네이버 액세스 토큰:', accessToken);
            
            if (!user || !accessToken) {
              reject(new Error('네이버 사용자 정보를 가져올 수 없습니다.'));
              return;
            }
            
            // user 객체가 이미 데이터를 포함하고 있는 경우 직접 접근
            const userInfo = {
              id: user.id || user.getId?.(),
              email: user.email || user.getEmail?.(),
              name: user.name || user.getName?.(),
              nickname: user.nickname || user.getNickname?.(),
              profileImage: user.profile_image || user.getProfileImage?.(),
              birthday: user.birthday || user.getBirthday?.(),
              birthyear: user.birthyear || user.getBirthyear?.(),
              gender: user.gender || user.getGender?.(),
              mobile: user.mobile || user.getMobile?.()
            };
            
            devLog('네이버 로그인 성공:', { userInfo, providerId: userInfo.id });
            
            resolve({
              accessToken: accessToken,
              userInfo: userInfo,
              providerId: userInfo.id
            });
          } catch (userError) {
            devError('네이버 사용자 정보 처리 실패:', userError);
            reject(new Error('네이버 사용자 정보 처리에 실패했습니다.'));
          }
        } else {
          reject(new Error('네이버 로그인 상태가 아닙니다.'));
        }
      });
    } catch (error) {
      devError('네이버 로그인 상태 확인 중 오류:', error);
      reject(error);
    }
  });
};

// 네이버 로그인
export const naverLogin = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    try {
      if (!window.naverLogin) {
        reject(new Error('네이버 SDK가 로드되지 않았습니다.'));
        return;
      }

      devLog('네이버 로그인 시작...');

      // 먼저 기존 로그인 상태를 확인
      window.naverLogin.getLoginStatus((status: boolean) => {
        devLog('현재 네이버 로그인 상태:', status);
        
        if (status) {
          // 이미 로그인된 상태라면 사용자 정보를 바로 반환
          getNaverUserInfo()
            .then(resolve)
            .catch((error) => {
              devError('기존 네이버 로그인 정보 처리 실패:', error);
              // 기존 정보 처리 실패 시 새로 로그인 진행
              startNaverLogin();
            });
        } else {
          // 로그인되지 않은 상태라면 새로 로그인 진행
          startNaverLogin();
        }
      });

      const startNaverLogin = () => {
        devLog('새로운 네이버 로그인 진행...');
        
        // 로그인 성공 후 콜백을 처리할 수 있도록 상태 저장
        sessionStorage.setItem('naverLoginInProgress', 'true');
        sessionStorage.setItem('naverLoginResolve', 'pending');
        
        // 네이버 로그인 실행 (리다이렉트 방식)
        window.naverLogin.authorize();
        
        // 리다이렉트가 발생하므로 여기서는 resolve하지 않음
        // 페이지가 리로드된 후 로그인 컴포넌트에서 처리됨
      };
      
    } catch (error) {
      devError('네이버 로그인 실행 중 오류:', error);
      reject(error);
    }
  });
};

// 네이버 로그아웃
export const naverLogout = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      if (!window.naverLogin) {
        reject(new Error('네이버 SDK가 로드되지 않았습니다.'));
        return;
      }

      window.naverLogin.logout();
      devLog('네이버 로그아웃 완료');
      resolve();
    } catch (error) {
      devError('네이버 로그아웃 실패:', error);
      reject(error);
    }
  });
};

// 네이버 연결 해제
export const naverUnlink = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      if (!window.naverLogin) {
        reject(new Error('네이버 SDK가 로드되지 않았습니다.'));
        return;
      }

      // 연결 해제는 네이버 개발자센터에서 별도로 구현해야 함
      devLog('네이버 연결 해제는 별도 구현이 필요합니다.');
      resolve();
    } catch (error) {
      devError('네이버 연결 해제 실패:', error);
      reject(error);
    }
  });
};
